import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  CalendarDays,
  Upload,
  Images,
  ScanFace,
  Users,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  Camera,
  Scissors,
} from 'lucide-react';
import { useUiStore } from '@/store/uiStore';

interface NavItem {
  to: string;
  labelKey: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  { to: '/', labelKey: 'nav.dashboard', icon: <LayoutDashboard className="h-[18px] w-[18px]" /> },
  { to: '/events', labelKey: 'nav.events', icon: <CalendarDays className="h-[18px] w-[18px]" /> },
  { to: '/upload', labelKey: 'nav.upload', icon: <Upload className="h-[18px] w-[18px]" /> },
  { to: '/gallery', labelKey: 'nav.gallery', icon: <Images className="h-[18px] w-[18px]" /> },
  { to: '/culling', labelKey: 'nav.culling', icon: <Scissors className="h-[18px] w-[18px]" /> },
  { to: '/faces', labelKey: 'nav.faces', icon: <ScanFace className="h-[18px] w-[18px]" /> },
  { to: '/people', labelKey: 'nav.people', icon: <Users className="h-[18px] w-[18px]" /> },
  { to: '/analysis', labelKey: 'nav.analysis', icon: <BarChart3 className="h-[18px] w-[18px]" /> },
  { to: '/settings', labelKey: 'nav.settings', icon: <Settings className="h-[18px] w-[18px]" /> },
];

export function Sidebar() {
  const { t } = useTranslation();
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);

  return (
    <aside
      className={`fixed inset-y-0 start-0 z-30 flex flex-col border-e border-gray-200 bg-white shadow-sm transition-all duration-300 dark:border-surface-border dark:bg-surface-sidebar dark:shadow-none ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      {/* Logo */}
      <div className={`flex h-14 items-center border-b border-gray-200 dark:border-surface-border ${collapsed ? 'justify-center px-0' : 'gap-3 px-4'}`}>
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-500">
          <Camera className="h-4 w-4 text-white" />
        </div>
        {!collapsed && (
          <span className="text-sm font-bold tracking-wide text-gray-900 dark:text-white">
            {t('app.name')}
          </span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            title={collapsed ? t(item.labelKey) : undefined}
            className={({ isActive }) =>
              `group relative flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150 ${
                isActive
                  ? 'bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-surface-hover dark:hover:text-gray-200'
              } ${collapsed ? 'justify-center' : 'gap-3'}`
            }
          >
            {({ isActive }) => (
              <>
                {/* Active indicator bar */}
                {isActive && (
                  <span className="absolute start-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-primary-500" />
                )}
                <span className={isActive ? 'text-primary-500 dark:text-primary-400' : ''}>
                  {item.icon}
                </span>
                {!collapsed && (
                  <span className="truncate">{t(item.labelKey)}</span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Collapse toggle */}
      <div className="border-t border-gray-200 p-2 dark:border-surface-border">
        <button
          onClick={toggleSidebar}
          className="flex w-full items-center justify-center rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-surface-hover dark:hover:text-gray-300"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </div>
    </aside>
  );
}
